/**
 * BrightPath API Client
 * Axios-based HTTP client with automatic token management
 * and queue-based concurrent request handling
 */

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

// =============================================================================
// Configuration
// =============================================================================

// Ensure API base URL always ends with /api/v1 (and avoid duplicating it)
const rawApiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
// Strip trailing slash and any existing /api/v1 suffix, then add it back
const normalizedUrl = rawApiUrl.replace(/\/+$/, '').replace(/\/api\/v1$/, '');
export const API_BASE_URL = `${normalizedUrl}/api/v1`;

// Debug: Log API configuration on startup (v2 - force rebuild)
if (typeof window !== 'undefined') {
  console.log('[API] Raw URL:', rawApiUrl);
  console.log('[API] Base URL:', API_BASE_URL);
  console.log('[API] Normalized from:', normalizedUrl);
}
const REFRESH_ENDPOINT = '/auth/refresh';
const TOKEN_KEY = 'brightpath_access_token';
const AUTH_STORE_KEY = 'brightpath-auth'; // Zustand persisted auth store key

// =============================================================================
// Types
// =============================================================================

export interface ApiError {
  message: string;
  code: string;
  status: number;
  details?: Record<string, unknown>;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface TokenPayload {
  sub: string;
  exp: number;
  type: 'access' | 'refresh';
  role: string;
}

// =============================================================================
// Token Management
// =============================================================================

export const tokenManager = {
  /**
   * Get the stored access token
   */
  get: (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Store the access token and reset redirect state
   */
  set: (token: string): void => {
    localStorage.setItem(TOKEN_KEY, token);
    // Reset redirect flag when a new token is set (user logged in again)
    isRedirecting = false;
  },

  /**
   * Remove the stored access token
   */
  remove: (): void => {
    localStorage.removeItem(TOKEN_KEY);
  },

  /**
   * Check if the stored token is valid (exists and not expired)
   */
  isValid: (): boolean => {
    const token = tokenManager.get();
    if (!token) return false;

    try {
      const payload = tokenManager.decode(token);
      if (!payload) return false;
      // Check if token expires in more than 30 seconds
      return payload.exp * 1000 > Date.now() + 30000;
    } catch {
      return false;
    }
  },

  /**
   * Decode JWT payload without verification
   */
  decode: (token: string): TokenPayload | null => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  },

  /**
   * Get user ID from token
   */
  getUserId: (): string | null => {
    const token = tokenManager.get();
    if (!token) return null;
    const payload = tokenManager.decode(token);
    return payload?.sub || null;
  },

  /**
   * Get user role from token
   */
  getUserRole: (): string | null => {
    const token = tokenManager.get();
    if (!token) return null;
    const payload = tokenManager.decode(token);
    return payload?.role || null;
  },
};

// =============================================================================
// API Client Instance
// =============================================================================

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 30000, // 30 second timeout
});

// =============================================================================
// Request Interceptor
// =============================================================================

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenManager.get();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// =============================================================================
// Response Interceptor with Queue-Based Token Refresh
// =============================================================================

let isRefreshing = false;
let isRedirecting = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

/**
 * Process queued requests after token refresh
 */
const processQueue = (error: unknown, token: string | null = null): void => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Don't retry if no config or already retried
    if (!originalRequest) {
      return Promise.reject(error);
    }

    // If we're already redirecting to login, reject all requests immediately
    if (isRedirecting) {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized
    // Don't attempt refresh for auth endpoints (prevents infinite loops)
    const isAuthEndpoint = originalRequest.url?.includes('/auth/');
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Use a fresh axios instance to avoid interceptor loops
        const { data } = await axios.post(
          `${API_BASE_URL}${REFRESH_ENDPOINT}`,
          {},
          { withCredentials: true }
        );

        const newToken = data.access_token;
        tokenManager.set(newToken);
        processQueue(null, newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Set redirecting flag to prevent further retry attempts
        isRedirecting = true;

        // Clear both token and persisted auth state to prevent redirect loops
        tokenManager.remove();
        localStorage.removeItem(AUTH_STORE_KEY);

        // Redirect to login (avoid if already on login page)
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // If it's a 401 on the refresh endpoint itself, clear tokens and redirect
    if (error.response?.status === 401 && isAuthEndpoint) {
      // Set redirecting flag to prevent further retry attempts
      isRedirecting = true;

      // Clear both token and persisted auth state to prevent redirect loops
      tokenManager.remove();
      localStorage.removeItem(AUTH_STORE_KEY);
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// =============================================================================
// Error Handling Utilities
// =============================================================================

/**
 * Parse API error response into standardized format
 */
export const parseApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ detail?: string; message?: string }>;
    return {
      message:
        axiosError.response?.data?.detail ||
        axiosError.response?.data?.message ||
        axiosError.message ||
        'An unexpected error occurred',
      code: axiosError.code || 'UNKNOWN_ERROR',
      status: axiosError.response?.status || 500,
      details: axiosError.response?.data as Record<string, unknown>,
    };
  }

  if (error instanceof Error) {
    return {
      message: error.message,
      code: 'CLIENT_ERROR',
      status: 0,
    };
  }

  return {
    message: 'An unexpected error occurred',
    code: 'UNKNOWN_ERROR',
    status: 0,
  };
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error: unknown): boolean => {
  if (axios.isAxiosError(error)) {
    return !error.response && error.code === 'ERR_NETWORK';
  }
  return false;
};

/**
 * Check if error is a timeout error
 */
export const isTimeoutError = (error: unknown): boolean => {
  if (axios.isAxiosError(error)) {
    return error.code === 'ECONNABORTED';
  }
  return false;
};

export default apiClient;
